import Phaser from 'phaser';
import { HAPTICS, MAX_SIM_STEPS_PER_FRAME, PALETTE, SIM_STEP, SPRINT } from '../../config';
import { AiRunner } from '../../game/AiRunner';
import { createAthleteView, type AthleteView } from '../../game/AthleteView';
import { AudioEngine } from '../../game/AudioEngine';
import { t } from '../../game/i18n';
import { getDiscipline } from '../../game/data/disciplines';
import { getSkin } from '../../game/data/skins';
import { pickOpponents } from '../../game/data/aiRoster';
import type { DisciplineResult, Standing } from '../../game/types';
import { Button } from '../../ui/Button';
import { PowerMeter } from '../../ui/PowerMeter';
import { TapZones } from '../../ui/TapZones';
import { viewport } from '../../ui/viewport';
import { DisciplineScene } from './DisciplineScene';

type Phase = 'intro' | 'running' | 'finished';
type Side = 'L' | 'R';

const START_X = 200;
const RACE_M = 100;
const HURDLE_FIRST = 14;
const HURDLE_GAP = 12;

interface Hurdle {
  dist: number;
  resolved: boolean;
  cleared: boolean;
  gfx: Phaser.GameObjects.Graphics;
}

/**
 * 110m Hurdles — the showcase event fusing both verbs into one rhythm: mash L/R to run,
 * tap SALTA to clear each hurdle in its timing window. Hit one and you stumble (big speed
 * penalty). Time-based, raced against the AI field in lanes.
 */
export class Hurdles extends DisciplineScene {
  private phase: Phase = 'intro';
  private rig!: AthleteView;
  private ai: AiRunner[] = [];

  private velocity = 0;
  private distance = 0;
  private elapsed = 0;
  private wrTime = 0;
  private wrLine!: Phaser.GameObjects.Graphics;
  private acc = 0;
  private topSpeed = 0;
  private lastSide: Side | null = null;
  private lastTapAt = 0;
  private cadence = 0;
  private maxSpeed: number = SPRINT.maxSpeed;
  private impulseScale = 1;

  private jumping = false;
  private jumpT = 0;
  private jumpDur = 0.5;
  private hurdles: Hurdle[] = [];

  private W = 0;
  private H = 0;
  private groundY = 0;
  private anchorX = 0;
  private uiS = 1;
  private lanes: number[] = [];
  private trackTopY = 0;

  private speedBar!: PowerMeter;
  private hudTime!: Phaser.GameObjects.Text;
  private bigText!: Phaser.GameObjects.Text;
  private jumpBtn!: Button;
  private tapZones!: TapZones;
  private crowd!: Phaser.GameObjects.TileSprite;
  private beatTimer?: Phaser.Time.TimerEvent;

  constructor() {
    super('Hurdles');
  }

  create(): void {
    super.create();
    this.phase = 'intro';
    this.velocity = 0; this.distance = 0; this.elapsed = 0; this.acc = 0; this.topSpeed = 0;
    this.lastSide = null; this.lastTapAt = 0; this.cadence = 0;
    this.jumping = false; this.jumpT = 0; this.hurdles = []; this.ai = [];

    const { w, h, s } = viewport(this);
    this.W = w; this.H = h; this.uiS = s; this.anchorX = w * 0.28;
    const laneCount = 4;
    const bottomY = h - Math.max(64, h * 0.09);
    const laneGap = Math.max(38, h * 0.06);
    this.lanes = Array.from({ length: laneCount }, (_, i) => bottomY - i * laneGap);
    this.groundY = this.lanes[0]!;
    this.trackTopY = this.lanes[laneCount - 1]! - laneGap * 0.7;
    this.wrLine = this.add.graphics().setDepth(3);
    this.fetchWorldRecord('hurdles', 'time', (m) => { this.wrTime = m; });

    const speedFactor = 0.7 + (this.athlete.stats.speed / 100) * 0.6;
    this.maxSpeed = SPRINT.maxSpeed * speedFactor;
    this.impulseScale = speedFactor;

    this.addBackground();
    this.drawTrack();
    this.buildAthletes();
    this.buildHud();
    this.controls.on('tapLeft', () => this.onTap('L'));
    this.controls.on('tapRight', () => this.onTap('R'));
    this.controls.on('action', () => this.tryJump());
    this.startCountdown();
  }

  private addBackground(): void {
    this.add.graphics().setScrollFactor(0).setDepth(-12).fillStyle(PALETTE.sky, 1).fillRect(0, 0, this.W, this.trackTopY + 40);
    const src = this.textures.get('stadium').getSourceImage();
    const scale = (this.trackTopY + 50) / src.height;
    this.crowd = this.add.tileSprite(0, 0, this.W, this.trackTopY + 50, 'stadium').setOrigin(0, 0).setScrollFactor(0).setDepth(-10).setTileScale(scale, scale);
  }

  private drawTrack(): void {
    const t = this.add.graphics().setScrollFactor(0).setDepth(-6);
    t.fillStyle(0x3f7d4f, 1).fillRect(0, this.trackTopY - 12, this.W, 16);
    t.fillStyle(PALETTE.laneAccent, 0.9).fillRect(0, this.trackTopY, this.W, 4);
    t.fillStyle(PALETTE.track, 1).fillRect(0, this.trackTopY + 4, this.W, this.H - this.trackTopY);
    for (let i = this.lanes.length - 1; i >= 0; i--) {
      const top = i === this.lanes.length - 1 ? this.trackTopY + 4 : this.lanes[i + 1]!;
      t.fillStyle(i % 2 === 0 ? 0x2f3a63 : PALETTE.track, 1).fillRect(0, top, this.W, this.lanes[i]! - top + 2);
      t.fillStyle(PALETTE.trackLine, 0.6).fillRect(0, this.lanes[i]!, this.W, 2);
    }
    const endX = START_X + (RACE_M + 6) * SPRINT.pixelsPerMeter;
    const g = this.add.graphics().setDepth(-5);
    for (let m = 0; m <= RACE_M; m += 10) {
      const x = START_X + m * SPRINT.pixelsPerMeter;
      this.add.text(x, this.groundY + 16, `${m}m`, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: '16px', color: '#aeb8db' }).setOrigin(0.5, 0);
    }
    // Hurdles in every lane: the player's lane is interactive; the others are decorative
    // (the AI hop over them). Far lanes are smaller/dimmer for depth.
    for (let d = HURDLE_FIRST; d <= RACE_M - 8; d += HURDLE_GAP) {
      const x = START_X + d * SPRINT.pixelsPerMeter;
      this.lanes.forEach((laneY, lane) => {
        const gfx = this.add.graphics().setDepth(lane === 0 ? 7 : 5 - lane);
        if (lane > 0) gfx.setAlpha(0.9 - lane * 0.05);
        this.drawHurdle(gfx, x, laneY, 1 - lane * 0.12, false);
        if (lane === 0) this.hurdles.push({ dist: d, resolved: false, cleared: false, gfx });
      });
    }
    // Finish.
    const fx = START_X + RACE_M * SPRINT.pixelsPerMeter;
    const fh = this.groundY - this.trackTopY;
    for (let i = 0; i < Math.max(6, Math.round(fh / 26)); i++) {
      g.fillStyle(i % 2 === 0 ? PALETTE.white : PALETTE.ink, 1).fillRect(fx - 7, this.trackTopY + 4 + i * 26, 14, 26);
    }
    this.cameras.main.setBounds(0, 0, endX + this.W, this.H);
  }

  private drawHurdle(g: Phaser.GameObjects.Graphics, x: number, laneY: number, scale: number, knocked: boolean): void {
    g.clear();
    const hH = Math.max(34, this.H * 0.07) * scale;
    const w = 24 * scale;
    if (knocked) {
      g.fillStyle(0xffffff, 0.9).fillRect(x - 4, laneY - 6, hH, 8);
      g.fillStyle(PALETTE.bad, 1).fillRect(x - 4, laneY - 6, hH, 4);
    } else {
      g.fillStyle(0xdfe6ff, 1).fillRect(x - 2, laneY - hH, 4 * scale, hH);
      g.fillStyle(0xdfe6ff, 1).fillRect(x - 2 + w, laneY - hH, 4 * scale, hH);
      g.fillStyle(PALETTE.laneAccent, 1).fillRect(x - 4, laneY - hH, w + 4, 8 * scale);
    }
  }

  private buildAthletes(): void {
    const displayH = Phaser.Math.Clamp(this.H * 0.34, 170, 320);
    const opponents = pickOpponents(getSkin(this.athlete.skin).id, this.lanes.length - 1);
    opponents.forEach((opp, i) => {
      const lane = i + 1;
      const aiH = displayH * (1 - lane * 0.12);
      const finishTime = Math.max(11, opp.sprintTime + 1.5 + Math.random() * 2.5);
      const r = new AiRunner(this, opp, finishTime, this.lanes[lane]!, aiH, START_X, RACE_M, SPRINT.pixelsPerMeter);
      r.view.container.setDepth(5 - lane).setAlpha(0.92 - lane * 0.05);
      this.ai.push(r);
    });
    this.rig = createAthleteView(this, START_X, this.groundY, this.athlete, displayH);
    this.rig.container.setDepth(6);
  }

  private buildHud(): void {
    const fs = (n: number) => `${Math.max(13, Math.round(n * this.uiS))}px`;
    this.hudTime = this.add.text(this.W / 2, 16, '0.00"', { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(40), color: '#f4f7ff', fontStyle: 'bold' })
      .setOrigin(0.5, 0).setScrollFactor(0).setShadow(0, 3, '#000000aa', 5);
    const barW = Math.min(280, this.W * 0.4);
    const topY = Math.max(50, this.H * 0.13);
    this.speedBar = new PowerMeter(this, 30 + barW / 2, topY, { width: barW, height: 18 });
    this.add.text(30 + barW / 2, topY - 18, t('hud.speed'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(12), color: '#d3dbf2' }).setOrigin(0.5).setScrollFactor(0);
    this.bigText = this.add.text(this.W / 2, this.H * 0.42, '', { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(96), color: '#ffd24a', fontStyle: 'bold' })
      .setOrigin(0.5).setScrollFactor(0).setShadow(0, 8, '#00000088', 12);
    const jumpLabel = this.sys.game.device.os.desktop ? `⬆ ${t('btn.jump')} ${t('btn.space')}` : `⬆ ${t('btn.jump')}`;
    this.jumpBtn = new Button(this, this.W / 2, this.H - Math.max(30, 38 * this.uiS), jumpLabel, () => this.tryJump(), {
      width: Math.min(200, this.W * 0.4), height: Math.max(52, 58 * this.uiS), fontSize: Math.max(18, Math.round(22 * this.uiS)), color: PALETTE.good,
    });
    this.jumpBtn.setScrollFactor(0).setAlpha(0.92).setVisible(false);
    this.tapZones = new TapZones(this);
  }

  private startCountdown(): void {
    ['3', '2', '1', t('game.go')].forEach((label, i) => {
      this.time.delayedCall(i * 800, () => {
        this.bigText.setText(label).setAlpha(1).setScale(1.5);
        this.tweens.add({ targets: this.bigText, scale: 1, duration: 350, ease: 'Back.out' });
        if (label === t('game.go')) {
          AudioEngine.go(); this.vibrate(HAPTICS.go);
          this.phase = 'running';
          this.controls.setEnabled(true);
          this.jumpBtn.setVisible(true);
          this.lastTapAt = this.time.now / 1000;
          this.tweens.add({ targets: this.bigText, alpha: 0, delay: 350, duration: 300 });
          this.beatTimer = this.time.addEvent({ delay: 1000 / ((SPRINT.idealCadenceMin + SPRINT.idealCadenceMax) / 2), loop: true, callback: () => { if (this.phase === 'running') AudioEngine.beat(); } });
        } else { AudioEngine.countdownTick(); this.vibrate(HAPTICS.countdownTick); }
      });
    });
  }

  private onTap(side: Side): void {
    if (this.phase !== 'running') return;
    const now = this.time.now / 1000;
    const dt = Math.max(0.001, now - this.lastTapAt);
    this.cadence = Phaser.Math.Linear(this.cadence, 1 / dt, 0.45);
    this.lastTapAt = now;
    const alternated = side !== this.lastSide;
    this.lastSide = side;
    let mult = alternated ? 1 : SPRINT.sameSidePenalty;
    if (this.cadence >= SPRINT.idealCadenceMin && this.cadence <= SPRINT.idealCadenceMax && alternated) mult *= SPRINT.flowBonus;
    else if (this.cadence > SPRINT.overspinThreshold) mult *= SPRINT.overspinPenalty;
    this.velocity = Math.min(this.maxSpeed * 1.06, this.velocity + SPRINT.tapImpulse * this.impulseScale * mult);
    this.tapZones.flash(side);
    AudioEngine.tap(alternated ? 0.6 : 0.2);
    this.vibrate(HAPTICS.tap);
  }

  /** Attempt to clear the next hurdle; good timing = clean leap, else just a hop. */
  private tryJump(): void {
    if (this.phase !== 'running' || this.jumping) return;
    this.jumping = true;
    this.jumpT = 0;
    const next = this.hurdles.find((hd) => !hd.resolved);
    if (next) {
      const gap = next.dist - this.distance;
      if (gap >= 0.2 && gap <= 2.6) {
        next.resolved = true;
        next.cleared = true; // clean clear, no penalty
        this.bigText.setText('HOP!').setFontSize(Math.round(40 * this.uiS)).setColor('#4ade80').setAlpha(1).setScale(0.7);
        this.tweens.add({ targets: this.bigText, alpha: 0, duration: 350 });
      }
    }
    AudioEngine.tap(0.8);
    this.vibrate(HAPTICS.goodTap);
  }

  update(_t: number, delta: number): void {
    const dt = delta / 1000;
    this.tapZones.update(dt);
    for (let i = 0; i < this.ai.length; i++) {
      const a = this.ai[i]!;
      a.update(this.elapsed, dt);
      // Auto-hop the AI over each hurdle in their lane.
      const laneY = this.lanes[i + 1]!;
      let arc = 0;
      for (const hd of this.hurdles) {
        const w = 1.0;
        if (a.distance > hd.dist - w && a.distance < hd.dist + w) {
          arc = Math.max(arc, Math.sin(((a.distance - (hd.dist - w)) / (2 * w)) * Math.PI) * this.H * 0.1 * (1 - (i + 1) * 0.12));
        }
      }
      a.view.container.y = laneY - arc;
    }
    if (this.phase === 'intro') { this.rig.update(dt); return; }
    if (this.phase === 'running') {
      this.acc += dt;
      let steps = 0;
      while (this.acc >= SIM_STEP && steps < MAX_SIM_STEPS_PER_FRAME) { this.stepSim(SIM_STEP); this.acc -= SIM_STEP; steps++; }
      if (this.time.now / 1000 - this.lastTapAt > SPRINT.cadenceWindow) this.cadence = Phaser.Math.Linear(this.cadence, 0, 0.08);
    }
    this.renderFrame(dt);
  }

  private stepSim(step: number): void {
    this.velocity *= SPRINT.dragPerStep;
    if (this.velocity < 0.01) this.velocity = 0;
    this.distance += this.velocity * step;
    this.elapsed += step;
    this.topSpeed = Math.max(this.topSpeed, this.velocity);
    // Resolve any hurdle we've reached.
    for (const hd of this.hurdles) {
      if (!hd.resolved && this.distance >= hd.dist) {
        hd.resolved = true;
        if (!this.jumping) {
          // Hit it — stumble.
          this.velocity *= 0.5;
          this.drawHurdle(hd.gfx, START_X + hd.dist * SPRINT.pixelsPerMeter, this.groundY, 1, true);
          this.cameras.main.shake(120, 0.005);
          this.cameras.main.flash(140, 255, 80, 80);
          this.vibrate(HAPTICS.finish);
        }
      }
    }
    if (this.distance >= RACE_M) { this.distance = RACE_M; this.finish(); }
  }

  /** Faint gold pace line moving at the world-record time. */
  private drawWr(): void {
    const g = this.wrLine;
    g.clear();
    if (this.wrTime <= 0) return;
    const p = Math.min(1, this.elapsed / this.wrTime);
    const x = START_X + RACE_M * SPRINT.pixelsPerMeter * p;
    g.fillStyle(PALETTE.sun, 0.4).fillRect(x - 1.5, this.trackTopY, 3, this.groundY - this.trackTopY + 12);
    g.fillStyle(PALETTE.sun, 0.9).fillTriangle(x - 7, this.trackTopY - 2, x + 7, this.trackTopY - 2, x, this.trackTopY + 8);
  }

  private renderFrame(dt: number): void {
    const worldX = START_X + this.distance * SPRINT.pixelsPerMeter;
    // Jump arc.
    let arc = 0;
    if (this.jumping) {
      this.jumpT += dt;
      const p = Phaser.Math.Clamp(this.jumpT / this.jumpDur, 0, 1);
      arc = Math.sin(p * Math.PI) * (this.H * 0.13);
      if (p >= 1) this.jumping = false;
    }
    this.rig.container.x = worldX;
    this.rig.container.y = this.groundY - arc;
    this.cameras.main.scrollX = Phaser.Math.Clamp(worldX - this.anchorX, 0, this.cameras.main.getBounds().width - this.W);
    const speedNorm = Phaser.Math.Clamp(this.velocity / this.maxSpeed, 0, 1);
    this.rig.setMotion(this.phase === 'finished' ? 0 : speedNorm);
    this.rig.update(dt);
    this.crowd.tilePositionX = this.cameras.main.scrollX * 0.12;
    this.speedBar.set(speedNorm);
    this.hudTime.setText(`${this.elapsed.toFixed(2)}"`);
    this.drawWr();
  }

  private finish(): void {
    if (this.phase === 'finished') return;
    this.phase = 'finished';
    this.controls.setEnabled(false);
    this.beatTimer?.remove();
    this.jumpBtn.setVisible(false);
    this.tapZones.setVisible(false);
    this.rig.setPose('celebrate');
    AudioEngine.fanfare();
    this.vibrate(HAPTICS.finish);
    this.cameras.main.shake(200, 0.006);

    const standings: Standing[] = [
      { name: this.athlete.name, species: this.athlete.species, isPlayer: true, nationality: this.athlete.nationality, metric: this.elapsed },
      ...this.ai.map((a) => ({ name: a.opponent.name, species: a.opponent.species, isPlayer: false, nationality: a.opponent.nationality, metric: a.finishTime })),
    ].sort((x, y) => x.metric - y.metric);
    const rank = standings.findIndex((s) => s.isPlayer) + 1;
    const score = Math.round(Phaser.Math.Clamp(1500 - this.elapsed * 60, 200, 1000));
    const reward = getDiscipline('hurdles').scoreToReward(score);
    const result: DisciplineResult = {
      disciplineId: 'hurdles', metricKind: 'time', athleteId: this.athlete.id, athleteName: this.athlete.name,
      species: this.athlete.species, nationality: this.athlete.nationality, metric: this.elapsed, score, reward, rank, standings, topSpeed: this.topSpeed,
    };
    this.bigText.setText(t('game.finish')).setFontSize(Math.round(60 * this.uiS)).setColor('#ffd24a').setAlpha(1).setScale(0.6);
    this.tweens.add({ targets: this.bigText, scale: 1, duration: 500, ease: 'Back.out' });
    this.time.delayedCall(2200, () => this.finishDiscipline(result));
  }

  private vibrate(pattern: number | number[]): void {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try { navigator.vibrate(pattern); } catch { /* unsupported */ }
    }
  }
}
